import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import useWebSocket from "react-use-websocket";

import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { showIncomingCallBlockedToast } from "./IncomingCallToast";

type SocketContextValue = Pick<
  ReturnType<typeof useWebSocket>,
  "sendJsonMessage" | "sendMessage" | "readyState" | "lastJsonMessage" | "lastMessage"
>;

const SocketContext = createContext<SocketContextValue | null>(null);

type BridgeMessagePayload = {
  sender: string;
  ts: number;
  message: unknown;
};

type SocketOwnerPayload = {
  owner: string;
  ts: number;
};

type IncomingCallDedupePayload = {
  eventKey: string;
  ts: number;
};

const SOCKET_OWNER_STALE_MS = 3500;
const SOCKET_OWNER_HEARTBEAT_MS = 1000;
const INCOMING_CALL_OPEN_CONFIRM_DELAY_MS = 80;
const INCOMING_CALL_DEDUPE_TTL_MS = 10000;
const INCOMING_CALL_PROCESS_WINDOW_MS = 4000;

const resolveEventType = (message: unknown) => {
  if (!message || typeof message !== "object") return null;
  const anyMessage = message as any;
  return anyMessage.event ?? anyMessage.type ?? anyMessage.event_type ?? null;
};

const resolveEventKey = (message: unknown) => {
  if (!message || typeof message !== "object") return null;
  const anyMessage = message as any;
  const key =
    anyMessage.event_id ??
    anyMessage.call_id ??
    anyMessage.CRT ??
    anyMessage.crt ??
    anyMessage.id ??
    anyMessage.ts ??
    anyMessage.timestamp ??
    null;
  if (key === null || key === undefined) return null;
  return String(key);
};

const resolveCustomerPetData = (message: unknown) => {
  if (!message || typeof message !== "object") return { customerId: null, petId: null, crtId: null };
  const anyMessage = message as any;
  const customerData = anyMessage.customer_pet_data ?? anyMessage.customerPetData ?? {};
  const normalizeNullableId = (value: unknown) => {
    if (value === null || value === undefined) return null;
    const reducedValue = String(value).trim();
    if (!reducedValue) return null;
    if (["null", "undefined"].includes(reducedValue.toLowerCase())) return null;
    return reducedValue;
  };
  return {
    customerId: normalizeNullableId(customerData.customer_id ?? customerData.customerId ?? null),
    petId: normalizeNullableId(customerData.pet_id ?? customerData.petId ?? null),
    crtId: normalizeNullableId(anyMessage?.CRT_ID ?? anyMessage?.CRT ?? anyMessage?.crt ?? null),
  };
};

const parseOwnerPayload = (rawValue: string | null): SocketOwnerPayload | null => {
  if (!rawValue) return null;
  try {
    const parsedValue = JSON.parse(rawValue) as Partial<SocketOwnerPayload>;
    if (!parsedValue || typeof parsedValue !== "object") return null;
    if (typeof parsedValue.owner !== "string") return null;
    if (typeof parsedValue.ts !== "number") return null;
    return {
      owner: parsedValue.owner,
      ts: parsedValue.ts,
    };
  } catch {
    return null;
  }
};

const parseIncomingCallDedupePayload = (rawValue: string | null): IncomingCallDedupePayload | null => {
  if (!rawValue) return null;
  try {
    const parsedValue = JSON.parse(rawValue) as Partial<IncomingCallDedupePayload>;
    if (!parsedValue || typeof parsedValue !== "object") return null;
    if (typeof parsedValue.eventKey !== "string") return null;
    if (typeof parsedValue.ts !== "number") return null;
    return {
      eventKey: parsedValue.eventKey,
      ts: parsedValue.ts,
    };
  } catch {
    // Backward compatibility with old plain-string storage.
    return {
      eventKey: rawValue,
      ts: 0,
    };
  }
};

const safeLocalStorageGet = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeLocalStorageSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures to keep socket flow alive.
  }
};

const safeLocalStorageRemove = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage failures to keep socket flow alive.
  }
};

const safeSessionStorageGet = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSessionStorageSet = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ignore storage failures to keep socket flow alive.
  }
};

const readIncomingCallDedupePayload = (key: string) => {
  const localPayload = parseIncomingCallDedupePayload(safeLocalStorageGet(key));
  if (localPayload) return localPayload;
  return parseIncomingCallDedupePayload(safeSessionStorageGet(key));
};

const writeIncomingCallDedupePayload = (key: string, payload: IncomingCallDedupePayload) => {
  const serializedPayload = JSON.stringify(payload);
  safeLocalStorageSet(key, serializedPayload);
  safeSessionStorageSet(key, serializedPayload);
};

// Runs `worker` under an exclusive, browser-wide lock keyed by `lockName` so
// that — across every tab of this origin — at most one context opens the call
// window for a given call. This is the decisive guard against multiple windows
// opening simultaneously for one inbound call: the localStorage dedupe/owner
// checks rely on timing windows and last-write-wins, which a true simultaneous
// race can slip past, whereas the Web Locks API grants the lock to exactly one
// caller and hands `null` to the rest atomically.
const runWithIncomingCallOpenLock = async (lockName: string, worker: () => void): Promise<void> => {
  const locks =
    typeof navigator !== "undefined" ? (navigator as unknown as { locks?: any }).locks : undefined;

  // Older browsers without the Web Locks API fall back to running directly; the
  // localStorage dedupe/lock guards remain the only protection there.
  if (!locks || typeof locks.request !== "function") {
    worker();
    return;
  }

  try {
    await locks.request(lockName, { ifAvailable: true }, async (lock: unknown) => {
      // A null lock means another tab already holds it for this call — that tab
      // owns the single window, so this one must not open a duplicate.
      if (!lock) return;
      worker();
    });
  } catch {
    // Never silently drop a call if the lock API misbehaves; the dedupe re-check
    // inside `worker` still prevents a duplicate in the common case.
    worker();
  }
};

const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const configuration = useConfiguration();
  const vmsUserId = useAppSelector((state) => state.auth?.vms_user_id);
  const authUserEmail = useAppSelector((state) => state?.auth?.user?.email);
  const userRole = useAppSelector((state) => state?.auth?.userPermissions?.role_name);
  const stateAccessToken = useAppSelector((state) => state?.auth?.authorization?.accessToken);

  const localAccessToken = safeLocalStorageGet(configuration.localStorage.ACCESS_TOKEN.key);
  const rawAccessToken = stateAccessToken || localAccessToken || "";
  const accessToken = rawAccessToken.replace(/^Bearer\s+/i, "").trim();

  const normalizeSocketUrl = (url: string | undefined) => {
    if (!url) return undefined;
    if (/^wss?:\/\//i.test(url)) return url;
    if (/^wss?:/i.test(url)) return url.replace(/^wss?:/i, (match) => `${match}//`);
    return `wss://${url}`;
  };

  // RBAC: SOCKET_EXOTEL_ROUTING — legacy role: sales_ops
  const isSalesOpps = useFeatureAccess("SOCKET_EXOTEL_ROUTING");
  // RBAC: SOCKET_INCOMING_CALL_HANDLER — legacy roles: clinic_ops, clinic_ops_doggyworld, sales_ops
  const canHandleIncomingCall = useFeatureAccess("SOCKET_INCOMING_CALL_HANDLER");
  const isExotelSocketFlow = isSalesOpps;

  // Click-log API is a hook, so it's resolved here and mirrored into a ref that
  // the incoming-call effect/closures can call without adding it to their deps.
  const { execute: logClickAPI } = useClickLogAPI();
  const logClickAPIRef = useRef(logClickAPI);
  useEffect(() => {
    logClickAPIRef.current = logClickAPI;
  }, [logClickAPI]);

  const socketBaseUrl = isSalesOpps
    ? normalizeSocketUrl(import.meta.env.VITE_EXOTEL_SOCKET_URL)
    : normalizeSocketUrl(import.meta.env.VITE_SOCKET_URL as string | undefined);

  const getInstanceId = () => {
    try {
      return crypto.randomUUID();
    } catch {
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  };

  const instanceIdRef = useRef<string>(getInstanceId());
  const channelRef = useRef<BroadcastChannel | null>(null);
  const [bridgeState, setBridgeState] = useState<{ ts: number; message: unknown } | null>(null);
  const [isSocketOwner, setIsSocketOwner] = useState(false);
  const isSocketOwnerRef = useRef(false);

  const resolvedVmsUserId = useMemo(() => {
    if (!vmsUserId) return "";
    if (typeof vmsUserId === "string") return vmsUserId.trim();
    if (typeof vmsUserId === "object") {
      return String(vmsUserId.user_id || vmsUserId.id || "").trim();
    }
    return "";
  }, [vmsUserId]);

  const currentSearch = typeof window !== "undefined" ? window.location.search : "";
  const isExotelPopupTab = useMemo(() => {
    if (!currentSearch) return false;
    return new URLSearchParams(currentSearch).get("isExotel") === "true";
  }, [currentSearch]);

  const wsUrl = useMemo(() => {
    if (!resolvedVmsUserId || !socketBaseUrl) return null;
    const base = socketBaseUrl.replace(/\/$/, "");
    return `${base}/${resolvedVmsUserId}`;
  }, [socketBaseUrl, resolvedVmsUserId]);

  const socketOwnerKey = useMemo(() => {
    if (!resolvedVmsUserId) return "";
    return `vetic-vms:socket-owner:${resolvedVmsUserId}`;
  }, [resolvedVmsUserId]);
  const incomingCallDedupeKey = useMemo(() => {
    if (!resolvedVmsUserId) return "vetic-vms:last-incoming-call";
    return `vetic-vms:last-incoming-call:${resolvedVmsUserId}`;
  }, [resolvedVmsUserId]);
  const incomingCallOpenLockKey = useMemo(() => {
    if (!resolvedVmsUserId) return "vetic-vms:incoming-call-open-lock";
    return `vetic-vms:incoming-call-open-lock:${resolvedVmsUserId}`;
  }, [resolvedVmsUserId]);

  useEffect(() => {
    isSocketOwnerRef.current = isSocketOwner;
  }, [isSocketOwner]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isExotelSocketFlow || !socketOwnerKey) {
      setIsSocketOwner(false);
      return;
    }

    let unmounted = false;

    const setOwnershipState = (nextValue: boolean) => {
      if (unmounted) return;
      if (isSocketOwnerRef.current === nextValue) return;
      isSocketOwnerRef.current = nextValue;
      setIsSocketOwner(nextValue);
    };

    const writeOwnership = (timestamp = Date.now()) => {
      const payload: SocketOwnerPayload = {
        owner: instanceIdRef.current,
        ts: timestamp,
      };
      safeLocalStorageSet(socketOwnerKey, JSON.stringify(payload));
    };

    const releaseOwnership = () => {
      const existingOwner = parseOwnerPayload(safeLocalStorageGet(socketOwnerKey));
      if (!existingOwner || existingOwner.owner !== instanceIdRef.current) return;
      safeLocalStorageRemove(socketOwnerKey);
    };

    const syncOwnership = () => {
      const now = Date.now();
      const currentOwner = parseOwnerPayload(safeLocalStorageGet(socketOwnerKey));

      if (currentOwner?.owner === instanceIdRef.current) {
        writeOwnership(now);
        setOwnershipState(true);
        return;
      }

      const isOwnerStale = !currentOwner || now - currentOwner.ts > SOCKET_OWNER_STALE_MS;
      if (isOwnerStale) {
        writeOwnership(now);
        const confirmedOwner = parseOwnerPayload(safeLocalStorageGet(socketOwnerKey));
        setOwnershipState(confirmedOwner?.owner === instanceIdRef.current);
        return;
      }

      setOwnershipState(false);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== socketOwnerKey) return;
      syncOwnership();
    };

    const handleUnload = () => {
      releaseOwnership();
    };

    const handleVisibilityOrFocus = () => {
      syncOwnership();
    };

    syncOwnership();
    window.addEventListener("storage", handleStorage);
    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("pagehide", handleUnload);
    window.addEventListener("focus", handleVisibilityOrFocus);
    document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    const timer = window.setInterval(syncOwnership, SOCKET_OWNER_HEARTBEAT_MS);

    return () => {
      unmounted = true;
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("pagehide", handleUnload);
      window.removeEventListener("focus", handleVisibilityOrFocus);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      window.clearInterval(timer);
      releaseOwnership();
      setIsSocketOwner(false);
      isSocketOwnerRef.current = false;
    };
  }, [socketOwnerKey, isExotelSocketFlow]);

  const socketQueryParams = useMemo(() => {
    return accessToken && isSalesOpps
      ? {
          token: accessToken,
        }
      : undefined;
  }, [accessToken, isSalesOpps]);

  const socketOptions = useMemo(
    () => ({
      queryParams: socketQueryParams,
      shouldReconnect: () => true,
      reconnectInterval: 2000,
      reconnectAttempts: Number.POSITIVE_INFINITY,
      retryOnError: true,
      share: true,
    }),
    [socketQueryParams],
  );

  // In Exotel flow we keep a single active socket per user (owner tab) and
  // bridge events to other tabs via BroadcastChannel to avoid server-side
  // connection thrashing when multiple tabs are open.
  const shouldConnect = Boolean(wsUrl) && (!isExotelSocketFlow || isSocketOwner);
  const { sendJsonMessage, sendMessage, readyState, lastJsonMessage: socketLastJsonMessage, lastMessage } =
    useWebSocket(wsUrl, socketOptions, shouldConnect);

  const socketMessageTsRef = useRef<number>(0);
  const prevSocketMessageRef = useRef<unknown>(undefined);
  if (socketLastJsonMessage !== prevSocketMessageRef.current && socketLastJsonMessage !== undefined) {
    prevSocketMessageRef.current = socketLastJsonMessage;
    if (socketLastJsonMessage !== null) {
      socketMessageTsRef.current = Date.now();
    }
  }

  const effectiveLastJsonMessage =
    bridgeState && bridgeState.ts > socketMessageTsRef.current ? bridgeState.message : socketLastJsonMessage;
  const shouldHandleIncomingCallInThisTab = isExotelSocketFlow ? isSocketOwner : !isExotelPopupTab;
  const isAuthenticatedSession = Boolean(accessToken);
  const prevEffectiveMessageRef = useRef<unknown>(undefined);
  const effectiveMessageReceivedAtRef = useRef<number>(0);
  if (effectiveLastJsonMessage !== prevEffectiveMessageRef.current && effectiveLastJsonMessage !== undefined) {
    prevEffectiveMessageRef.current = effectiveLastJsonMessage;
    if (effectiveLastJsonMessage !== null) {
      effectiveMessageReceivedAtRef.current = Date.now();
    }
  }

  const lastIncomingCallRef = useRef<IncomingCallDedupePayload | null>(null);
  useEffect(() => {
    if (!effectiveLastJsonMessage) return;
    if (Date.now() - effectiveMessageReceivedAtRef.current > INCOMING_CALL_PROCESS_WINDOW_MS) return;
    if (!shouldHandleIncomingCallInThisTab) return;
    if (!isAuthenticatedSession) return;
    if (!canHandleIncomingCall) return;

    const eventType = resolveEventType(effectiveLastJsonMessage);
    if (!eventType || String(eventType).toUpperCase() !== "INCOMING_CALL") return;

    const { customerId, petId, crtId } = resolveCustomerPetData(effectiveLastJsonMessage);

    const eventKey =
      resolveEventKey(effectiveLastJsonMessage) ??
      `${String(eventType)}-${customerId ?? "add-customer"}-${petId ?? "add-pet"}`;
    const now = Date.now();
    const recentInMemoryEvent =
      lastIncomingCallRef.current?.eventKey === eventKey &&
      now - (lastIncomingCallRef.current?.ts ?? 0) <= INCOMING_CALL_DEDUPE_TTL_MS;
    if (recentInMemoryEvent) return;

    const storedEvent = readIncomingCallDedupePayload(incomingCallDedupeKey);
    const isRecentStoredEvent =
      storedEvent?.eventKey === eventKey && now - (storedEvent?.ts ?? 0) <= INCOMING_CALL_DEDUPE_TTL_MS;
    if (isRecentStoredEvent) return;

    lastIncomingCallRef.current = { eventKey, ts: now };

    if (typeof window === "undefined") return;


    const openIncomingCallWindow = () => {
      // Re-check the persistent dedupe inside the exclusive lock: another tab
      // may have already opened the window for this call while we waited for the
      // lock to be granted.
      const storedEvent = readIncomingCallDedupePayload(incomingCallDedupeKey);
      const isRecentStoredEvent =
        storedEvent?.eventKey === eventKey &&
        Date.now() - (storedEvent?.ts ?? 0) <= INCOMING_CALL_DEDUPE_TTL_MS;
      if (isRecentStoredEvent) return;
      writeIncomingCallDedupePayload(incomingCallDedupeKey, {
        eventKey,
        ts: Date.now(),
      });

      const rawPhoneNumber =
        typeof effectiveLastJsonMessage === "object" && effectiveLastJsonMessage !== null
          ? (effectiveLastJsonMessage as any).phone_number
          : null;
      const normalizedRawPhoneNumber =
        rawPhoneNumber === null || rawPhoneNumber === undefined ? null : String(rawPhoneNumber).trim() || null;
      const rawDidNumber =
        typeof effectiveLastJsonMessage === "object" && effectiveLastJsonMessage !== null
          ? ((effectiveLastJsonMessage as any).did_number ??
            (effectiveLastJsonMessage as any).didNumber ??
            (effectiveLastJsonMessage as any).did ??
            null)
          : null;
      const normalizedDidNumber =
        rawDidNumber === null || rawDidNumber === undefined ? null : String(rawDidNumber).trim() || null;
      try {
        const existingExotelInfoRaw = safeSessionStorageGet("exotel_information_current");
        const existingExotelInfo =
          existingExotelInfoRaw && existingExotelInfoRaw !== ""
            ? (JSON.parse(existingExotelInfoRaw) as Record<string, any>)
            : null;

        const mergedExotelInfo = {
          ...(existingExotelInfo && typeof existingExotelInfo === "object" ? existingExotelInfo : {}),
          event: "incoming_call",
          customer_pet_data: {
            customer_id: customerId ?? existingExotelInfo?.customer_pet_data?.customer_id ?? null,
            pet_id: petId ?? existingExotelInfo?.customer_pet_data?.pet_id ?? null,
          },
          rawPhoneNumber: normalizedRawPhoneNumber ?? existingExotelInfo?.rawPhoneNumber ?? null,
          did_number: normalizedDidNumber ?? existingExotelInfo?.did_number ?? null,
          CRT: crtId ?? existingExotelInfo?.CRT ?? null,
        };

        safeSessionStorageSet("exotel_information_current", JSON.stringify(mergedExotelInfo));
        if (mergedExotelInfo.CRT) {
          safeSessionStorageSet(`exotel_information:${mergedExotelInfo.CRT}`, JSON.stringify(mergedExotelInfo));
        }
      } catch {
        // Ignore sessionStorage failures and continue with navigation.
      }
      const phoneQueryParam =
        rawPhoneNumber !== null && rawPhoneNumber !== undefined
          ? `&phone_number=${encodeURIComponent(String(rawPhoneNumber))}`
          : "";

      const encodedCRT = encodeURIComponent(crtId ?? "");
      let destinationPath = `/app/customer/profile/add?isExotel=true&CRT=${encodedCRT}${phoneQueryParam}`;
      if (customerId && petId) {
        destinationPath = `/app/customer/profile/customer-details/appointments/${customerId}/${petId}?isExotel=true&CRT=${encodedCRT}`;
      } else if (customerId) {
        destinationPath = `/app/customer/profile/customer-details/appointments/${customerId}?isExotel=true&CRT=${encodedCRT}&new_user=true`;
      }
      // Reuse a single browser tab per call. The localStorage dedupe/owner-lock
      // above already tries to keep a call to one tab, but any residual double
      // handling (a second socket owner during an election flap, a re-sent
      // INCOMING_CALL frame, or a re-open after the dedupe TTL expires on a
      // long-ringing call) would otherwise stack a fresh tab. Targeting a
      // stable, per-call window name makes the browser reuse the same tab.
      // IMPORTANT: a non-empty window name is only honoured when "noopener" is
      // NOT set — with noopener the browser treats every named target as
      // "_blank" and always opens a new tab. The destination is same-origin, so
      // omitting noopener is safe here.
      const callWindowName = `exotel_incoming_call_${String(crtId ?? eventKey).replace(
        /[^a-zA-Z0-9_-]/g,
        "_",
      )}`;
      const callWindowUrl = `${window.location.origin}${destinationPath}`;

      // Try to open the call window automatically. When popups are allowed this
      // succeeds silently; when blocked, window.open() returns null (or an
      // immediately-closed window), which is our signal to prompt the agent.
      const performOpen = () => {
        try {
          return window.open(callWindowUrl, callWindowName);
        } catch {
          return null;
        }
      };

      const popup = performOpen();
      const wasBlocked = !popup || popup.closed;
      if (!wasBlocked) return;

      // Runs from the toast's "Open call" click (a real user gesture). It retries
      // the popup — which normally succeeds now that it's gesture-driven — and if
      // the browser STILL blocks it (popups hard-disabled), it navigates the
      // current tab to the call screen instead, so the agent always reaches the
      // call. NOTE: browsers do not expose any API to programmatically enable the
      // popup permission; the toast copy asks the agent to allow it for future
      // calls, and this redirect covers the meantime.
      const openOrRedirect = () => {
        const reopened = performOpen();
        const didReopen = Boolean(reopened) && !reopened?.closed;

        // Log the "Open call" click with full call context BEFORE any in-tab
        // redirect, so the request is dispatched before navigation can abort it.
        void logClickAPIRef.current({
          event_type: "SOCKET_INCOMING_CALL",
          event_action: didReopen
            ? "popup_blocked_open_click_reopened"
            : "popup_blocked_open_click_redirected",
          direction: "inbound",
          source: isExotelSocketFlow ? "exotel" : "vms",
          cta_text: "Open call",
          crt_id: crtId ?? undefined,
          client_id: customerId ?? undefined,
          pet_id: petId ?? undefined,
          agent_id: authUserEmail || undefined,
          phone: normalizedRawPhoneNumber ?? undefined,
          mobile: normalizedRawPhoneNumber ?? undefined,
          request_id: eventKey,
          timestamp: Date.now(),
          opened_at: Date.now(),
        });

        if (!didReopen) {
          window.location.assign(callWindowUrl);
        }
      };

      // Log that the blocked-popup toast was shown, with full call context. This
      // is the "impression" that pairs with the "Open call" click log above, so
      // the funnel (blocked → shown → clicked/redirected) can be measured.
      void logClickAPIRef.current({
        event_type: "SOCKET_INCOMING_CALL",
        event_action: "popup_blocked_toast_shown",
        direction: "inbound",
        source: isExotelSocketFlow ? "exotel" : "vms",
        cta_text: "Open call",
        crt_id: crtId ?? undefined,
        client_id: customerId ?? undefined,
        pet_id: petId ?? undefined,
        agent_id: authUserEmail || undefined,
        phone: normalizedRawPhoneNumber ?? undefined,
        mobile: normalizedRawPhoneNumber ?? undefined,
        request_id: eventKey,
        timestamp: Date.now(),
        opened_at: Date.now(),
      });

      // Popup blocked — surface a prompt whose button click reopens from a real
      // user gesture (falling back to an in-tab redirect) and nudges the agent to
      // allow popups for future calls.
      showIncomingCallBlockedToast({
        eventKey,
        phoneNumber: normalizedRawPhoneNumber,
        onOpen: openOrRedirect,
        onDismiss: () => {
          void logClickAPIRef.current({
            event_type: "SOCKET_INCOMING_CALL",
            event_action: "popup_blocked_toast_dismissed",
            direction: "inbound",
            source: isExotelSocketFlow ? "exotel" : "vms",
            cta_text: "Open call",
            crt_id: crtId ?? undefined,
            client_id: customerId ?? undefined,
            pet_id: petId ?? undefined,
            agent_id: authUserEmail || undefined,
            phone: normalizedRawPhoneNumber ?? undefined,
            mobile: normalizedRawPhoneNumber ?? undefined,
            request_id: eventKey,
            timestamp: Date.now(),
            opened_at: Date.now(),
          });
        },
      });
    };

    const lockTimer = window.setTimeout(() => {
      // Only the socket-owning tab drives the open in the Exotel flow.
      if (isExotelSocketFlow && !isSocketOwnerRef.current) return;
      // Serialize the open across every tab for this specific call so exactly
      // one window is ever created, even under a simultaneous multi-tab race.
      void runWithIncomingCallOpenLock(`${incomingCallOpenLockKey}:${eventKey}`, openIncomingCallWindow);
    }, INCOMING_CALL_OPEN_CONFIRM_DELAY_MS);

    return () => {
      window.clearTimeout(lockTimer);
    };
  }, [
    effectiveLastJsonMessage,
    userRole,
    shouldHandleIncomingCallInThisTab,
    incomingCallDedupeKey,
    incomingCallOpenLockKey,
    isExotelSocketFlow,
    isAuthenticatedSession,
  ]);

  useEffect(() => {
    if (!resolvedVmsUserId) return;
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(`vetic-vms:socket:${resolvedVmsUserId}`);
    channelRef.current = channel;

    const handleBridgeMessage = (event: MessageEvent) => {
      const data = event.data as BridgeMessagePayload;
      if (!data || typeof data !== "object") return;
      if (data.sender === instanceIdRef.current) return;
      if (typeof data.ts !== "number") return;
      setBridgeState((prev) => {
        const prevTs = prev?.ts ?? 0;
        if (data.ts <= prevTs) return prev;
        if (data.ts <= socketMessageTsRef.current) return prev;
        return { ts: data.ts, message: data.message };
      });
    };

    channel.addEventListener("message", handleBridgeMessage);
    return () => {
      channel.removeEventListener("message", handleBridgeMessage);
      channel.close();
      if (channelRef.current === channel) {
        channelRef.current = null;
      }
    };
  }, [resolvedVmsUserId]);

  useEffect(() => {
    if (socketLastJsonMessage === null || socketLastJsonMessage === undefined) return;
    const channel = channelRef.current;
    if (!channel) return;
    const payload: BridgeMessagePayload = {
      sender: instanceIdRef.current,
      ts: socketMessageTsRef.current || Date.now(),
      message: socketLastJsonMessage,
    };
    channel.postMessage(payload);
  }, [socketLastJsonMessage]);

  const value = useMemo(
    () => ({
      sendJsonMessage,
      sendMessage,
      readyState,
      lastJsonMessage: effectiveLastJsonMessage,
      lastMessage,
    }),
    [sendJsonMessage, sendMessage, readyState, effectiveLastJsonMessage, lastMessage],
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within SocketProvider");
  }
  return context;
};

export { SocketProvider, useSocket };
